import React, { useState } from "react";
import { message } from "antd";
import { useSubscriptionMutation } from "api/pricingApi";
import { getMetaAttributionPayload, trackMeta } from "utils/metaPixel";
import { BackButton, StepButtonRow } from "../styles";
import {
  AlertBanner,
  BillingToggle,
  FeatureItem,
  FeatureList,
  MainLayout,
  PlanButton,
  PlanCard,
  PlanDescription,
  PlanGrid,
  PlanTitle,
  PriceLine,
  StyledCategory,
  StyledHeading,
  StyledPrice,
  StyledSubHeading,
  TrialNote,
} from "./styles";

const MONTHLY_PRICES = {
  BASIC: 29.99,
  PRO: 49.99,
};

const PLANS = [
  {
    type: "BASIC",
    title: "Starter",
    description:
      "Everything you need to get started with finding new winning products",
    features: [
      "14-day free trial",
      "Automated order updates",
      "Automated order fulfillment",
      "Auto-checkout",
      "24/7 chat support",
    ],
  },
  {
    type: "PRO",
    title: "Pro",
    description: "The most popular plan if you are serious about getting started",
    featured: true,
    features: [
      "14-day free trial",
      "Everything in Starter, plus private source requests",
      "TikTok Shop weekly trending report",
      "Appeal management service",
      "24/7 1:1 account manager support",
    ],
  },
];

const yearlyPrice = (monthlyPrice) =>
  Number((monthlyPrice * 12 * 0.85).toFixed(2)).toFixed(2);

const FourthStep = ({ Back, showCancelledMessage = false }) => {
  const [billingCycle, setBillingCycle] = useState("MONTHLY");
  const [loadingPlanKey, setLoadingPlanKey] = useState(null);
  const [subscribe] = useSubscriptionMutation();

  const getPlanPrice = (type) =>
    billingCycle === "YEARLY"
      ? yearlyPrice(MONTHLY_PRICES[type])
      : MONTHLY_PRICES[type].toFixed(2);

  const handleSubscribe = async (type) => {
    const plan = billingCycle === "YEARLY" ? "YEARLY" : "MONTHLY";
    const currentPlanKey = `${type}-${plan}-STRIPE`;
    setLoadingPlanKey(currentPlanKey);

    trackMeta(
      "InitiateCheckout",
      {
        content_name: `${type}_${plan}`,
        currency: "USD",
        value: Number(getPlanPrice(type)),
      }
    );

    const response = await subscribe({
      type,
      plan,
      gateway: "STRIPE",
      onboarding: true,
      ...getMetaAttributionPayload(),
    });

    if (response?.error) {
      setLoadingPlanKey(null);
      message.error(
        response?.error?.data?.message || "Unable to start checkout. Please try again."
      );
      return;
    }

    if (response?.data?.link) {
      window.location.href = response.data.link;
    } else {
      setLoadingPlanKey(null);
      message.error("Checkout link was not returned. Please try again.");
    }
  };

  return (
    <MainLayout>
      <StyledHeading>Choose Your Plan</StyledHeading>
      <StyledSubHeading>
        Select a billing tier and add a payment method to activate your 14-day free
        trial. You will not be charged until the trial ends.
      </StyledSubHeading>

      {showCancelledMessage && (
        <AlertBanner>
          Checkout was cancelled. Select a plan below to continue activating your
          free trial.
        </AlertBanner>
      )}

      <BillingToggle
        value={billingCycle}
        onChange={setBillingCycle}
        options={[
          { label: "Monthly", value: "MONTHLY" },
          { label: "Yearly (15% off)", value: "YEARLY" },
        ]}
      />

      <PlanGrid>
        {PLANS.map((plan) => {
          const planKey = `${plan.type}-${billingCycle}-STRIPE`;
          const isLoading = loadingPlanKey === planKey;

          return (
            <PlanCard key={plan.type} $featured={plan.featured}>
              <PlanTitle>{plan.title}</PlanTitle>
              <PlanDescription>{plan.description}</PlanDescription>
              <PriceLine>
                <StyledPrice>${getPlanPrice(plan.type)}</StyledPrice>
                <StyledCategory>
                  /{billingCycle === "YEARLY" ? "Year" : "Month"}
                </StyledCategory>
              </PriceLine>
              <TrialNote>14-day free trial. Cancel anytime.</TrialNote>
              <FeatureList>
                {plan.features.map((feature) => (
                  <FeatureItem key={feature}>{feature}</FeatureItem>
                ))}
              </FeatureList>
              <PlanButton
                type="primary"
                loading={isLoading}
                disabled={Boolean(loadingPlanKey) && !isLoading}
                onClick={() => handleSubscribe(plan.type)}
              >
                {isLoading ? "Redirecting..." : "Start Free Trial"}
              </PlanButton>
            </PlanCard>
          );
        })}
      </PlanGrid>

      <StepButtonRow>
        <BackButton type="default" onClick={Back} disabled={Boolean(loadingPlanKey)}>
          Back
        </BackButton>
      </StepButtonRow>
    </MainLayout>
  );
};

export default FourthStep;
